import { z } from 'zod';
import { ActivationId, parseActivationId } from './activation.ts';
import { Id, IsoDateTime, JsonObject, JsonSchemaObject, TenantId } from './common.ts';
import { Deadline } from './deadline.ts';
import { ResourceScope } from './entitlement.ts';
import { Escalation } from './escalation.ts';

/**
 * Added by 17c1 (D3, ADR-015 amendment 3): the human work-item primitive. A `human` activity without a gate becomes
 * a `WorkItem`: opened by process code for one activation, claimed and completed by an identity that holds the
 * activity's `ownerRole` (pack role from configuration or the IdP) and the `act` entitlement on the item's scope,
 * with an output validated against a pack-registered schema. Completion signals the run
 * (`workItemSignalName(activationId)`, payload `WorkItemCompletion`) and projects the activity. Packs never
 * re-implement this authorization.
 */

export const WorkItemState = z
  .enum(['open', 'claimed', 'completed', 'cancelled'])
  .meta({ id: 'WorkItemState' });
export type WorkItemState = z.infer<typeof WorkItemState>;

export const WorkItem = z
  .object({
    workItemId: Id,
    tenantId: TenantId,
    processInstanceId: Id,
    caseId: Id.optional(),
    activationId: ActivationId,
    title: z.string().min(1).max(300),
    description: z.string().max(4000).optional(),
    /** Pack role a holder needs (`ActivityDefinition.ownerRole` unless the opener narrows it). */
    ownerRole: z.string().min(1),
    /**
     * Resource scope a holder needs `act` on (and viewers `view`): the instance scope, optionally with extra
     * pack-declared dimensions (narrower), never wider.
     */
    scope: ResourceScope,
    /** Pack-registered output schema (`registerWorkItemOutputSchema`); absent = any JSON object. */
    outputSchemaRef: z.string().min(1).optional(),
    /** Context shown to the holder (no secrets; it is visible to every viewer of the item). */
    input: JsonObject.default({}),
    state: WorkItemState,
    claimedBy: z.string().min(1).optional(),
    claimedAt: IsoDateTime.optional(),
    completedBy: z.string().min(1).optional(),
    completedAt: IsoDateTime.optional(),
    output: JsonObject.optional(),
    cancelReason: z.string().optional(),
    dueAt: IsoDateTime.optional(),
    /**
     * Added by 21 step 0 (§1, §1b.1, §1b.9): narrows who may claim to one principal (internal or external). For an
     * external principal this is the only holder path (`act` implied for this item only). Never approval authority.
     */
    assigneePrincipalId: z.string().min(1).optional(),
    /** Added by 21 step 0: further roles whose holders may claim (narrowing only, never authority; §1b.1). */
    participants: z.array(z.object({ role: z.string().min(1) })).optional(),
    /** Added by 21 step 0: the business deadline (the engine wait carries it as timeout; `dueAt` = `deadline.at`). */
    deadline: Deadline.optional(),
    /** Added by 21 step 0: what runs when the deadline passes (before the handler sees the timeout). */
    escalation: Escalation.optional(),
    /** Added by 21 step 0: true once the deadline passed while the item was open (a flag, not a state). */
    overdue: z.boolean().optional(),
    /**
     * Added by 21b (§1b.8, D-P8): the gate of a gated human activity. Completing the item opens an approval request
     * for it (`approvalRequestId`); the item stays `claimed` with its output until the gate decides, the activation
     * is `awaiting_approval`, and the run is signalled only after the decision.
     */
    gateId: z.string().min(1).optional(),
    /** Added by 21b: the approval request a gated completion opened (current revision). */
    approvalRequestId: Id.optional(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'WorkItem' });
export type WorkItem = z.infer<typeof WorkItem>;

/**
 * Payload of signal `human:<activationId>`: the work item of that activation was completed (or, review B1,
 * cancelled: `state: 'cancelled'`, empty `output`, `completedBy` = who cancelled, `reason`), so the run never hangs.
 */
export const WorkItemCompletion = z
  .object({
    workItemId: Id,
    activationId: ActivationId,
    state: z.enum(['completed', 'cancelled']).default('completed'),
    completedBy: z.string().min(1),
    output: JsonObject,
    reason: z.string().optional(),
    at: IsoDateTime,
  })
  .meta({ id: 'WorkItemCompletion' });
export type WorkItemCompletion = z.infer<typeof WorkItemCompletion>;

/** Signal name that delivers a work item's completion to its run: `human:<activationId>`. */
export function workItemSignalName(activationId: string): string {
  parseActivationId(activationId);
  return `human:${activationId}`;
}

// -----------------------------------------------------------------------------------------------
// Added by 21 step 0 (brief 21 §1, §2; ADR-021)
// -----------------------------------------------------------------------------------------------

/** JSON Schema extension keys on served work-item output and process input schemas. */
export const X_CLASSIFICATION = 'x-classification';
export const X_UI = 'x-ui';

/** `x-classification` of a schema field: `pii` fields are masked for viewers without `act` (21c item 6). */
export const FieldClassification = z.enum(['pii', 'internal', 'public']).meta({ id: 'FieldClassification' });
export type FieldClassification = z.infer<typeof FieldClassification>;

/** `x-ui` of a schema field: widget hint and display order. */
export const FieldUiHint = z
  .object({ widget: z.string().min(1).max(50).optional(), order: z.number().int().optional() })
  .meta({ id: 'FieldUiHint' });
export type FieldUiHint = z.infer<typeof FieldUiHint>;

/** `GET /work-items/{workItemId}` (21b): the item with its served output schema (never a bundled copy). */
export const WorkItemView = WorkItem.extend({
  /** JSON Schema (draft 2020-12) from `z.toJSONSchema`, with per-field `x-classification` and `x-ui`. */
  outputSchema: JsonSchemaObject.optional(),
  outputSchemaVersion: z.string().min(1).optional(),
}).meta({ id: 'WorkItemView' });
export type WorkItemView = z.infer<typeof WorkItemView>;

/** Body of `POST /work-items/{workItemId}/assign`; `null` clears the assignee. Replay identity stays the activation. */
export const AssignWorkItemBody = z
  .object({
    assigneePrincipalId: z.string().min(1).nullable(),
    reason: z.string().min(1).max(2000).optional(),
  })
  .meta({ id: 'AssignWorkItemBody' });
export type AssignWorkItemBody = z.infer<typeof AssignWorkItemBody>;

export const WORK_ITEM_EVENT_TYPES = { overdue: 'work-item.overdue' } as const;

/** `work-item.overdue`: emitted by the orchestrator on the deadline timeout, before the escalation runs. */
export const WorkItemOverdueData = z
  .object({
    workItemId: Id,
    tenantId: TenantId,
    processInstanceId: Id,
    caseId: Id.optional(),
    activationId: ActivationId,
    deadline: Deadline,
    escalation: Escalation.optional(),
    /** Set when the deadline passed during a hold (§1b.3). */
    whileHeld: z.object({ holdId: Id, heldMs: z.number().int().nonnegative() }).optional(),
    commandId: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'WorkItemOverdueData' });
export type WorkItemOverdueData = z.infer<typeof WorkItemOverdueData>;

// -----------------------------------------------------------------------------------------------
// Added by 21b (brief 21 §1 "schemas are served, not bundled")
// -----------------------------------------------------------------------------------------------

/** A work-item output schema built from Zod: what `registerWorkItemOutputSchema` stores and serves. */
export interface ServedWorkItemOutputSchema {
  ref: string;
  validate: (output: Record<string, unknown>) => boolean;
  /** JSON Schema (draft 2020-12, `z.toJSONSchema`) with `x-classification` and `x-ui` on every top-level field. */
  jsonSchema: Record<string, unknown>;
  version: string;
}

export interface WorkItemSchemaOptions {
  /** Schema version served with the schema (default `1`). */
  version?: string;
  /** Per-field classification overrides (else the field's own `.meta({ 'x-classification' })`, else `internal`). */
  classification?: Record<string, FieldClassification>;
  /** Per-field UI hints (else the field's own `.meta({ 'x-ui' })`, else `{ order: <declaration index> }`). */
  ui?: Record<string, FieldUiHint>;
}

/**
 * Builds the served form of a pack's Zod output schema: `validate` is `schema.safeParse(o).success`, `jsonSchema`
 * comes from `z.toJSONSchema` (input side) and every top-level property carries `x-classification` and `x-ui`.
 */
export function workItemOutputSchemaFromZod(
  ref: string,
  schema: z.ZodType,
  options: WorkItemSchemaOptions = {},
): ServedWorkItemOutputSchema {
  const json = z.toJSONSchema(schema, { io: 'input', unrepresentable: 'any' }) as Record<string, unknown>;
  const properties = json.properties;
  if (properties && typeof properties === 'object') {
    Object.entries(properties as Record<string, Record<string, unknown>>).forEach(([field, prop], index) => {
      const classification =
        options.classification?.[field] ??
        FieldClassification.safeParse(prop[X_CLASSIFICATION]).data ??
        'internal';
      prop[X_CLASSIFICATION] = classification;
      const ownUi = FieldUiHint.safeParse(prop[X_UI]).data;
      prop[X_UI] = { order: index, ...(ownUi ?? {}), ...(options.ui?.[field] ?? {}) };
    });
  }
  return {
    ref,
    validate: (output) => schema.safeParse(output).success,
    jsonSchema: json,
    version: options.version ?? '1',
  };
}
